import {ActionIcon, Group, Menu, Table, TextInput} from "@mantine/core";
import {IconSearch, IconTrash} from "@tabler/icons-react";
import {useCallback, useRef, useState} from "react";
import {isRomaji, toHiragana, toRomaji} from "wanakana";
import {getKanjiSuggestions} from "../lib/kanjiApi";
import type {BatchRow} from "../types";

export function BatchEntryRowJapaneseVocab(props: {
  row: BatchRow;
  onUpdate: (
    id: string,
    field: keyof BatchRow | Partial<BatchRow>,
    value?: string,
  ) => void;
  onRemove: (id: string) => void;
}) {
  const {row, onUpdate, onRemove} = props;

  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [menuOpened, setMenuOpened] = useState(false);
  const expressionRef = useRef(row.expression);

  const handleSuggestKanji = useCallback(async () => {
    const textToSearch = row.expression;
    if (!textToSearch) return;

    expressionRef.current = textToSearch;
    setMenuOpened(true);
    setIsSearching(true);
    const hira = toHiragana(textToSearch);
    const results = await getKanjiSuggestions(hira);
    setSuggestions(results);
    setIsSearching(false);
  }, [row.expression]);

  const handleSelectSuggestion = useCallback(
    (kanji: string) => {
      const searchTerm = expressionRef.current;
      const currentField1 = row.field1;

      if (searchTerm && currentField1 === "") {
        const hira = toHiragana(searchTerm);
        let romajiValue = "";
        if (isRomaji(searchTerm)) {
          romajiValue = toRomaji(hira) || "";
        } else if (hira !== kanji && hira !== searchTerm) {
          romajiValue = toRomaji(hira) || "";
        }
        if (romajiValue) {
          onUpdate(row.id, {expression: kanji, field1: romajiValue});
        } else {
          onUpdate(row.id, "expression", kanji);
        }
      } else {
        onUpdate(row.id, "expression", kanji);
      }

      setMenuOpened(false);
    },
    [row.field1, onUpdate, row.id],
  );

  return (
    <Table.Tr>
      <Table.Td>
        <Group gap={4} wrap="nowrap">
          <TextInput
            value={row.expression}
            onChange={e => onUpdate(row.id, "expression", e.target.value)}
            placeholder="猫"
            variant="unstyled"
            required={row.meaning !== ""}
            style={{flexGrow: 1, fontSize: "1.5rem"}}
          />
          <Menu shadow="md" width={200} opened={menuOpened}>
            <Menu.Target>
              <ActionIcon
                size="sm"
                variant="subtle"
                loading={isSearching}
                onClick={() => {
                  if (row.expression) handleSuggestKanji();
                }}
              >
                <IconSearch size={14} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Suggestions</Menu.Label>
              {suggestions.length > 0 ? (
                suggestions.map(s => (
                  <Menu.Item key={s} onClick={() => handleSelectSuggestion(s)}>
                    {s}
                  </Menu.Item>
                ))
              ) : (
                <Menu.Item disabled>No suggestions</Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Table.Td>
      <Table.Td>
        <TextInput
          value={row.field1}
          onChange={e => onUpdate(row.id, "field1", e.target.value)}
          placeholder="neko"
          variant="unstyled"
        />
      </Table.Td>
      <Table.Td>
        <TextInput
          value={row.meaning}
          onChange={e => onUpdate(row.id, "meaning", e.target.value)}
          placeholder="Cat"
          variant="unstyled"
          required={row.expression !== ""}
        />
      </Table.Td>
      <Table.Td>
        <TextInput
          value={row.auxiliary}
          onChange={e => onUpdate(row.id, "auxiliary", e.target.value)}
          placeholder="noun"
          variant="unstyled"
        />
      </Table.Td>
      <Table.Td>
        <ActionIcon
          color="red"
          variant="subtle"
          onClick={() => onRemove(row.id)}
          title="Remove row"
        >
          <IconTrash size={16} />
        </ActionIcon>
      </Table.Td>
    </Table.Tr>
  );
}
